import { editor, editors } from 'Cloud/Application/Editor';
import { EditorID } from 'Cloud/Application/Editor/types';
import config from 'Cloud/config';
import { cloneAttach, getMessage } from 'reactApp/api/axios.corsapi';
import { itemV4ToV2 } from 'reactApp/api/helpers/apiV4Helpers';
import { API_V4, APP_TITLE, CHOSEN_EDITOR, IS_B2B_BIZ_USER, IS_WEBVIEW, USER_EMAIL } from 'reactApp/appHelpers/configHelpers';
import { isAttachSaveToAllDocuments } from 'reactApp/appHelpers/featuresHelpers';
import { isEditingRestrictedABEnabled } from 'reactApp/appHelpers/featuresHelpers/features/editingDocumentRestrictions';
import { USE_PATH_FROM_HOME_PARAM } from 'reactApp/appHelpers/featuresHelpers/features/usePathFromHomeParam';
import { getRequestParamHome } from 'reactApp/appHelpers/settingsHelpers';
import { ALL_DOCUMENTS_FOLDER_ID, MAIL_ATTACHES_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { parseAttachId } from 'reactApp/modules/attaches/attaches.helpers';
import { handleReadyCoEditPublicAlert } from 'reactApp/modules/editor/sagas/coEditPublicAlert';
import { EditorRestrictionsActions } from 'reactApp/modules/editorRestrictions/editorRestrictions.module';
import { getParent } from 'reactApp/modules/file/utils';
import { loadHomeFolderRequest } from 'reactApp/modules/home/home.actions';
import { loadHomeFolder } from 'reactApp/modules/home/home.saga';
import { checkUpdateLicenseRequest } from 'reactApp/modules/profile/profile.module';
import { loadFolderRequest, loadPublicFileSuccess } from 'reactApp/modules/public/public.actions';
import { routeStatusPage } from 'reactApp/modules/router/router.module';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { EStatus } from 'reactApp/sections/ErrorPage/ErrorPage.types';
import { openEditorRestrictionModal } from 'reactApp/ui/EditorRestriction/EditorRestrictionModal/EditorRestrictionModal.helper';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';

import { EditorRestrictionsSelectors } from '../editorRestrictions/editorRestrictions.selectors';
import { composeAvailableEditors, composeExtensions, composeSharedExtensions, normalizeEditorId } from './editor.helpers';
import { initEditor, readyEditor as readyEditorAction, startEditor, startEditorRequest } from './editor.module';

const serverSideFolder = config.get('serverSideFolders');

export function* setEditor() {
    const extensions = composeExtensions(editors);
    const availableEditors = composeAvailableEditors(editors, extensions);
    const hasSharedEditor = editors.some((editor) => 'x-in-shared-folder' in editor);
    const sharedExtensions = composeSharedExtensions(editors, extensions);

    yield put(initEditor({ availableEditors, hasSharedEditor, sharedExtensions, extensions }));
}

function* handleStartEditorRequest(action: ReturnType<typeof startEditorRequest>) {
    // Где был вызван редактор по роутингу - хомяк или паблик
    const storage = action.payload.storage;
    let id = USE_PATH_FROM_HOME_PARAM ? yield call(getRequestParamHome) : yield select(getCurrentRouteId);

    const isEditorAttach = /^\/attaches\//.test(id);
    id = normalizeEditorId(id);

    const storageRoute = yield select(getCurrentStorage);
    const current = CHOSEN_EDITOR || {};

    if (storage !== EStorageType.public) {
        if (storageRoute === EStorageType.myofficeAttaches || isEditorAttach) {
            const { messageId, partId } = parseAttachId(id);

            yield getMessage({
                messageId,
                email: USER_EMAIL,
            });

            const res = yield cloneAttach({
                ids: [`${messageId};${partId}`],
                folder: isAttachSaveToAllDocuments ? ALL_DOCUMENTS_FOLDER_ID : MAIL_ATTACHES_FOLDER_ID,
                email: USER_EMAIL,
            });

            id = res[0];
        }

        yield loadHomeFolder({ id, isFolder: false, force: true });
    }

    if (API_V4 && storage === EStorageType.public) {
        const item = itemV4ToV2(serverSideFolder);
        yield put(loadPublicFileSuccess(item));

        if (item.home) {
            yield put(loadHomeFolderRequest({ id: getParent(item.home), isFolder: true }));
        }

        yield put(
            startEditor({
                item: {
                    ...item,
                    id,
                    subKind: item.subkind,
                    readOnly: item.readonly,
                    weblink_access_rights: item.weblinkAccessRights,
                    weblink_expires: item.weblinkExpires,
                    weblink_domestic: item.weblinkDomestic,
                },
            })
        );
        yield put(loadFolderRequest({ storage, id: item?.weblink }));
    }

    const item = yield select(getStorageItemById, storage, id);

    if (!item) {
        yield put(routeStatusPage({ status: EStatus.NOT_FOUND }));
        return;
    }

    if (isEditingRestrictedABEnabled) {
        let editorId = current.id;
        if (!editorId) {
            const editorFor = editor.getEditorsFor(item);
            editorId = editorFor[0]?.id;
        }
        yield put(EditorRestrictionsActions.checkEditingDisabled({ isR7: editorId === EditorID.R7 }));
        yield take(EditorRestrictionsActions.setEditingDisabled);
        const isEditingDisabled = yield select(EditorRestrictionsSelectors.isEditingRestricted);
        if (isEditingDisabled) {
            yield openEditorRestrictionModal({});
        }
    }

    yield editor.start(item);

    yield put(checkUpdateLicenseRequest());

    const titleDelimiter = IS_WEBVIEW ? '' : ' / ';
    const titleSuffix = IS_B2B_BIZ_USER ? APP_TITLE : 'Облако Mail';
    const title = item.name + titleDelimiter;
    document.title = (title || '') + (IS_WEBVIEW ? '' : titleSuffix);
}

export function* watchEditor() {
    yield takeLatest(readyEditorAction, handleReadyCoEditPublicAlert);
    yield takeLatest(startEditorRequest, handleStartEditorRequest);
}
